import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Settings, LogOut, Shield } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { logout } from '../../lib/auth'
import { cn } from '../../lib/utils'

export default function UserMenu() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'owner' || user?.role === 'admin'

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  const initials = user?.name
    ?.split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??'

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-label="User menu"
        aria-expanded={open}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-bg-hover transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center text-xs font-bold">
          {initials}
        </div>
        <span className="text-sm text-text-primary hidden lg:inline">{user?.name}</span>
      </button>

      {open && (
        <div role="menu" aria-label="User options" className="absolute right-0 top-full mt-2 w-56 bg-bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium text-text-primary">{user?.name}</p>
            <p className="text-xs text-text-secondary">{user?.email}</p>
            <p className="text-xs text-text-muted mt-0.5 capitalize">{user?.role}</p>
          </div>

          <div className="py-1">
            <MenuButton
              icon={Settings}
              label="Settings"
              onClick={() => { navigate('/settings'); setOpen(false) }}
            />
            {isAdmin && (
              <MenuButton
                icon={Shield}
                label="Admin"
                onClick={() => { navigate('/admin'); setOpen(false) }}
              />
            )}
          </div>

          <div className="py-1 border-t border-border">
            <MenuButton
              icon={LogOut}
              label="Sign out"
              onClick={async () => { setOpen(false); await logout(); navigate('/login') }}
              danger
            />
          </div>
        </div>
      )}
    </div>
  )
}

function MenuButton({ icon: Icon, label, onClick, danger }: {
  icon: React.ComponentType<{ size?: number }>
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors',
        danger
          ? 'text-severity-critical hover:bg-severity-critical/10'
          : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
      )}
    >
      <Icon size={16} />
      {label}
    </button>
  )
}
